<template>
  <div class="swap-card">
    <!-- 转出 -->
    <div class="sw-row" :class="{ 'is-venue': dir === 'out' }">
      <template v-if="dir === 'in'">
        <div class="swr-l">
          <i class="swr-dir">{{ t('me.tfOut') }}</i>
          <span class="swr-tag">{{ t('me.tfCentral') }}</span>
          <b class="swr-name">{{ mainAcct }}</b>
        </div>
        <div class="swr-r">
          <span>{{ t('me.tfAvailable') }}</span>
          <b>{{ formatCentral(centralAvail) }}</b>
        </div>
      </template>
      <template v-else>
        <button type="button" class="swr-l swr-pick" @click="emit('openVenue')">
          <i class="swr-dir">{{ t('me.tfOut') }}</i>
          <span class="swr-tag">{{ t('me.tfVenue') }}</span>
          <b class="swr-name">
            {{ venue.n }}
            <em v-if="tag" class="tfv-tag" :class="tag.cls">{{ tag.text }}</em>
            <i class="sw-caret">▾</i>
          </b>
        </button>
        <div class="swr-r">
          <span>{{ t('me.tfVenueBal') }}</span>
          <b>{{ formatVenueAmt(venue) }}</b>
        </div>
      </template>
    </div>

    <!-- 调向 -->
    <div class="sw-mid">
      <i class="sw-line" />
      <button type="button" class="sw-swap" :aria-label="t('me.tfSwapAria')" @click="emit('swap')">
        <AppIcon name="me-transfer" />
      </button>
      <i class="sw-line" />
    </div>

    <!-- 转入 -->
    <div class="sw-row" :class="{ 'is-venue': dir === 'in' }">
      <template v-if="dir === 'in'">
        <button type="button" class="swr-l swr-pick" @click="emit('openVenue')">
          <i class="swr-dir">{{ t('me.tfIn') }}</i>
          <span class="swr-tag">{{ t('me.tfVenue') }}</span>
          <b class="swr-name">
            {{ venue.n }}
            <em v-if="tag" class="tfv-tag" :class="tag.cls">{{ tag.text }}</em>
            <i class="sw-caret">▾</i>
          </b>
        </button>
        <div class="swr-r">
          <span>{{ t('me.tfVenueBal') }}</span>
          <b>{{ formatVenueAmt(venue) }}</b>
        </div>
      </template>
      <template v-else>
        <div class="swr-l">
          <i class="swr-dir">{{ t('me.tfIn') }}</i>
          <span class="swr-tag">{{ t('me.tfCentral') }}</span>
          <b class="swr-name">{{ mainAcct }}</b>
        </div>
        <div class="swr-r">
          <span>{{ t('me.tfAvailable') }}</span>
          <b>{{ formatCentral(centralAvail) }}</b>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TransferDir, TransferVenue } from '~/utils/transfer/types'

const props = defineProps<{
  dir: TransferDir
  venue: TransferVenue
  centralAvail: number
  mainAcct: string
  formatCentral: (n: number) => string
  formatVenueAmt: (v: TransferVenue) => string
}>()

const emit = defineEmits<{
  swap: []
  openVenue: []
}>()

const { t } = useI18n()

const tag = computed(() => {
  if (props.venue.mnt) return { cls: 'mnt', text: t('me.tfMaint') }
  if (props.venue.pend) return { cls: 'pend', text: t('me.tfPending') }
  return null
})
</script>

<style scoped lang="scss">
.swap-card {
  background: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: 16px;
  padding: 4px 14px;
}

.sw-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0;
}

.swr-l {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.swr-dir {
  font-style: normal;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--muted);
  opacity: 0.7;
  letter-spacing: 1px;
}

.swr-tag {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--muted);
  margin-top: 1px;
}

.swr-name {
  font-size: 15px;
  font-weight: 800;
  line-height: 1.35;
  color: var(--text);
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.swr-pick {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.swr-r {
  text-align: right;
  flex: 0 0 auto;
  padding-bottom: 1px;

  span {
    display: block;
    font-size: 10px;
    line-height: 1.3;
    color: var(--muted);
    margin-bottom: 3px;
  }

  b {
    font-size: 13px;
    font-weight: 800;
    line-height: 1.3;
    color: var(--gold-hi, #ffd274);
  }
}

.tfv-tag {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  color: #fff;

  &.mnt {
    background: #5a6478;
  }

  &.pend {
    background: #c48a3a;
  }
}

.sw-caret {
  font-style: normal;
  color: var(--primary);
  font-size: 12px;
  line-height: 1;
}

.sw-mid {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sw-line {
  flex: 1;
  height: 1px;
  background: var(--hairline);
  font-style: normal;
}

.sw-swap {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 50%;
  background: rgba(var(--primary-rgb), 0.18);
  border: 1px solid var(--primary);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 17px;
  cursor: pointer;
  position: relative;
  z-index: 1;

  &:active {
    transform: scale(0.92);
  }

  :deep(.app-icon svg) {
    width: 17px;
    height: 17px;
    transform: rotate(90deg);
  }
}
</style>
